/**
 * Product attributes in the Intemediate Product Representation
 *
 * NOTE: the interface matches the response format of the company-mapping service
 * => Make sure to keep them in sync when doing any changes
 */
export interface IPRAttributes {
  Tangibility: Tangibility;
  OfferingType: OfferingType;
  TypeOfBusinessTarget: string[]; // not used currently
  isManufactured: boolean;
  Certifications: CertificateType[];
  EnergySourcesUsed: EnergySource[];
  Recyclability: Recyclability[];
  MaterialsAbsences: AbsentRawMaterialType[]; // not assigned by the company-mapping service
  Educational: boolean;
  ForHumanitarianAid: boolean; // not assigned by the company-mapping service
  ForDisabledPeople: boolean; // not assigned by the company-mapping service
  ForLanguageMinorityGroups: boolean; // not assigned by the company-mapping service
  FollowingJournalisticEthics: boolean; // not assigned by the company-mapping service
  TargetIndustry: TargetIndustry[];
  IsPhysicalRetail: boolean;
  IsOnlineRetail: boolean;
  IsWholesale: boolean;
  IsRental: boolean;
  IsLeasing: boolean; // not assigned by the company-mapping service
  IsTrading: boolean;
  IsInstallationOf: boolean;
  IsAssemblyOf: boolean; // not assigned by the company-mapping service
  IsRepairOrMaintenanceOf: boolean;
  IsDistributionOf: boolean; // not assigned by the company-mapping service
  IsLicensingOf: boolean; // not assigned by the company-mapping service
  InDevelopingCountries: boolean;
  WithoutAnimalTesting: boolean; // not assigned by the company-mapping service
  UltraLowCostCommerce: boolean; // not assigned by the company-mapping service
}

/**
 * Tangibility types that can be detected
 *
 * NOTE: the values of the enum match the values provided by the company-mapping service
 * => Make sure to keep the values in sync
 */
export enum Tangibility {
  Physical = 'Physical',
  Digital = 'Digital',
}

/**
 * Offering types that can be detected
 *
 * NOTE: the values of the enum match the values provided by the company-mapping service
 * => Make sure to keep the values in sync
 */
export enum OfferingType {
  Product = 'Product',
  Service = 'Service',
}

/**
 * Relevant certificates that can be detected
 *
 * NOTE: the values of the enum match the values provided by the company-mapping service
 * => Make sure to keep the values in sync
 */
export enum CertificateType {
  LEED = 'LEED',
  BREEAM = 'BREEAM',
  ASC = 'ASC',
  FSC = 'FSC',
  PEFC = 'PEFC',
  SFI = 'SFI', // not assigned by the company-mapping service
  ATFS = 'ATFS', // not assigned by the company-mapping service
  HQE = 'HQE', // not assigned by the company-mapping service
  GOTS = 'GOTS',
  BirdFriendlyCoffee = 'Bird Friendly Coffee', // not assigned by the company-mapping service
  CertifiedHumane = 'Certified Humane', // not assigned by the company-mapping service
  DemeterBiodynamic = 'Demeter Biodynamic', // not assigned by the company-mapping service
  Fairtrade = 'Fairtrade',
  FriendOfTheSea = 'Friend of the Sea', // not assigned by the company-mapping service
  GlobalGAP = 'GlobalG.A.P.', // not assigned by the company-mapping service
  MSC = 'MSC',
  NonGMOProjectVerified = 'Non-GMO Project', // not assigned by the company-mapping service
  Organic = 'Organic', // not assigned by the company-mapping service
  RainforestAlliance = 'Rainforest Alliance',
  RSPO = 'RSPO', // not assigned by the company-mapping service
  RTRS = 'RTRS', // not assigned by the company-mapping service
  SoilAssociationOrganic = 'Soil Association', // not assigned by the company-mapping service
  ROC = 'ROC', // not assigned by the company-mapping service
  Bluesign = 'Bluesign', // not assigned by the company-mapping service
  FairWearFoundation = 'Fair Wear Foundation', // not assigned by the company-mapping service
  OEKOTEXMadeInGreen = 'OEKO-TEX Made in Green', // not assigned by the company-mapping service
  OEKOTEXStandard100 = 'OEKO-TEX Standard 100',
  SA8000 = 'SA8000', // not assigned by the company-mapping service
  WRAP = 'WRAP', // not assigned by the company-mapping service
  GRS = 'GRS', // not assigned by the company-mapping service
  RCS = 'RCS', // not assigned by the company-mapping service
  LWG = 'LWG', // not assigned by the company-mapping service
  RWS = 'RWS', // not assigned by the company-mapping service
  RDS = 'RDS', // not assigned by the company-mapping service
  COSMOS = 'COSMOS', // not assigned by the company-mapping service
  Ecocert = 'Ecocert', // not assigned by the company-mapping service
  EUEcolabel = 'EU Ecolabel', // not assigned by the company-mapping service
  LeapingBunny = 'Leaping Bunny', // not assigned by the company-mapping service
  NordicSwanEcolabel = 'Nordic Swan', // not assigned by the company-mapping service
  PETACrueltyFreeAndVegan = 'PETA', // not assigned by the company-mapping service
  SaferChoice = 'Safer Choice', // not assigned by the company-mapping service
  EnergyStar = 'Energy Star', // not assigned by the company-mapping service
  EPEAT = 'EPEAT', // not assigned by the company-mapping service
  TCO = 'TCO', // not assigned by the company-mapping service
  DGNB = 'DGNB', // not assigned by the company-mapping service
  GreenGlobes = 'Green Globes', // not assigned by the company-mapping service
  LivingBuildingChallenge = 'Living Building Challenge', // not assigned by the company-mapping service
  WELLBuildingStandard = 'WELL', // not assigned by the company-mapping service
  CradleToCradle = 'Cradle to Cradle', // not assigned by the company-mapping service
  FairForLife = 'Fair for Life', // not assigned by the company-mapping service
}

/**
 * Certificate descriptions for display purposes
 */
export const CERTIFICATE_DESCRIPTIONS: Record<CertificateType, string> = {
  [CertificateType.LEED]: 'Leadership in Energy and Environmental Design',
  [CertificateType.BREEAM]: 'Building Research Establishment Environmental Assessment Method',
  [CertificateType.ASC]: 'Aquaculture Stewardship Council',
  [CertificateType.FSC]: 'Forest Stewardship Council',
  [CertificateType.PEFC]: 'Programme for the Endorsement of Forest Certification',
  [CertificateType.SFI]: 'Sustainable Forestry Initiative',
  [CertificateType.ATFS]: 'American Tree Farm System',
  [CertificateType.HQE]: 'Haute Qualité Environnementale',
  [CertificateType.GOTS]: 'Global Organic Textile Standard',
  [CertificateType.BirdFriendlyCoffee]: '',
  [CertificateType.CertifiedHumane]: '',
  [CertificateType.DemeterBiodynamic]: '',
  [CertificateType.Fairtrade]: '',
  [CertificateType.FriendOfTheSea]: 'Friend of the Sea',
  [CertificateType.GlobalGAP]: 'GlobalG.A.P.',
  [CertificateType.MSC]: 'Marine Stewardship Council',
  [CertificateType.NonGMOProjectVerified]: 'Non-GMO Project Verified',
  [CertificateType.Organic]: 'EU Organic / USDA Organic',
  [CertificateType.RainforestAlliance]: 'Rainforest Alliance',
  [CertificateType.RSPO]: 'Roundtable on Sustainable Palm Oil',
  [CertificateType.RTRS]: 'Round Table on Responsible Soy',
  [CertificateType.SoilAssociationOrganic]: 'Soil Association Organic',
  [CertificateType.ROC]: 'Regenerative Organic Certified',
  [CertificateType.Bluesign]: 'Bluesign',
  [CertificateType.FairWearFoundation]: 'Fair Wear Foundation',
  [CertificateType.OEKOTEXMadeInGreen]: 'OEKO-TEX Made in Green',
  [CertificateType.OEKOTEXStandard100]: 'OEKO-TEX Standard 100',
  [CertificateType.SA8000]: 'Social Accountability Standard',
  [CertificateType.WRAP]: 'Worldwide Responsible Accredited Production',
  [CertificateType.GRS]: 'Global Recycled Standard',
  [CertificateType.RCS]: 'Recycled Claim Standard',
  [CertificateType.LWG]: 'Leather Working Group',
  [CertificateType.RWS]: 'Responsible Wool Standard',
  [CertificateType.RDS]: 'Responsible Down Standard',
  [CertificateType.COSMOS]: 'Organic / Natural',
  [CertificateType.Ecocert]: 'Ecocert',
  [CertificateType.EUEcolabel]: 'EU Ecolabel',
  [CertificateType.LeapingBunny]: 'Cruelty-Free',
  [CertificateType.NordicSwanEcolabel]: 'Nordic Swan Ecolabel',
  [CertificateType.PETACrueltyFreeAndVegan]: 'Cruelty-Free & Vegan',
  [CertificateType.SaferChoice]: 'US EPA Safer Choice',
  [CertificateType.EnergyStar]: 'Energy Star',
  [CertificateType.EPEAT]: 'Electronics Sustainability',
  [CertificateType.TCO]: 'TCO Certified',
  [CertificateType.DGNB]: 'DGNB',
  [CertificateType.GreenGlobes]: 'Green Globes',
  [CertificateType.LivingBuildingChallenge]: 'Living Building Challenge',
  [CertificateType.WELLBuildingStandard]: 'WELL Building Standard',
  [CertificateType.CradleToCradle]: 'Cradle to Cradle',
  [CertificateType.FairForLife]: 'Fair for Life',
};

/**
 * Target industries that can be detected
 *
 * NOTE: the values of the enum match the values provided by the company-mapping service
 * => Make sure to keep the values in sync
 */
export enum TargetIndustry {
  Agricultural = 'Agricultural industry',
  Aviation = 'Aviation industry',
  Beauty = 'Beauty industry',
  Chemicals = 'Chemicals industry',
  Clothing = 'Clothing industry',
  Education = 'Education industry',
  Entertainment = 'Entertainment industry',
  Telecommunications = 'Telecommunications industry',
  Electronics = 'Electronics industry',
  Restaurant = 'Restaurant industry',
  FinancialServices = 'Financial services industry',
  FoodAndBeverage = 'Food and beverage industry',
  FossilFuels = 'Fossil fuels industry',
  Healthcare = 'Healthcare industry',
  Maritime = 'Maritime industry',
  Packaging = 'Packaging industry',
  Plastics = 'Plastics industry',
  RenewableEnergy = 'Renewable energy industry',
  Retail = 'Retail industry',
  Travel = 'Travel industry',
  Mining = 'Mining industry',
  Pharmaceutical = 'Pharmaceutical industry',
  TransportationAndLogistics = 'Transportation and logistics industry',
  CargoShipping = 'Cargo shipping industry',
  WasteManagement = 'Waste management and recycling industry',
  ForestryPulpAndPaper = 'Forestry, pulp and paper industry',
  CementAndConcrete = 'Cement and concrete industry',
  NuclearEnergy = 'Nuclear energy industry',
  Bioenergy = 'Bioenergy industry',
  Space = 'Space industry',
  AlcoholicBeverages = 'Alcoholic beverages industry',
  SecurityAndDefence = 'Security and defence industry',
  SocialMedia = 'Social media industry',
  DigitalInfrastructureAndAI = 'Digital infrastructure and AI industry',
  ContentPublishing = 'Content publishing industry',
  FishingAndAquaculture = 'Fishing and aquaculture industry',
  MeatAndDairy = 'Meat and dairy industry',
  HumanResources = 'Human resources industry',
  Metals = 'Metals industry',
  RealEstateAndConstruction = 'Real estate and construction industry',
}

/**
 * Energy sources that can be detected
 *
 * NOTE: the values of the enum match the values provided by the company-mapping service
 * => Make sure to keep the values in sync
 */
export enum EnergySource {
  FossilFuels = 'Fossil Fuels',
  Nuclear = 'Nuclear',
  Renewables = 'Renewables',
  Bioenergy = 'Bioenergy',
}

/**
 * Recyclability options that can be detected
 *
 * NOTE: the values of the enum match the values provided by the company-mapping service
 * => Make sure to keep the values in sync
 */
export enum Recyclability {
  MonoMaterial = 'Mono-material',
  Biodegradable = 'Biodegradable',
  Reusable = 'Reusable',
  Repairable = 'Repairable',
  Modular = 'Modular (easy disassembly)',
  RecycledMaterials = 'Made from recycled materials',
  Refurbished = 'Refurbished',
}

/**
 * Relevant absent raw materials that can be detected
 *
 * NOTE: the values of the enum match the values provided by the company-mapping service
 * => Make sure to keep the values in sync
 */
export enum AbsentRawMaterialType {
  ParabenFree = 'Paraben-free',
  LeadFree = 'Lead-free',
  PalmOilFree = 'Palm oil-free',
  PlasticFree = 'Plastic-free',
  PFASFree = 'PFAS-free',
  PhthalateFree = 'Phthalate-free',
  HeavyMetalFree = 'Heavy-metal free',
  FragranceFree = 'Fragrance-free',
  MercuryFree = 'Mercury‑free',
  AmmoniaFree = 'Ammonia-free',
  CadmiumFree = 'Cadmium-free',
  FormaldehydeFree = 'Formaldehyde-free',
}

// Note: INDUSTRY_TO_PRODUCT_CODE and ENERGY_SOURCE_TO_PRODUCT_CODE
// are exported from ./ipr-to-logical-expressions directly to avoid circular dependency
